// MediaViewer — the job photo lightbox: open one, page through the rest,
// download the one you are looking at.
//
// ── What this restores (docs/PARITY_2026-09-08.md §16) ──────────────────────
//
// The classic panel opened a photo in a viewer with Previous / Next / Close, a
// caption, an "n of m" counter and a Download control. The rebuild wrapped
// each thumbnail in `<a target="_blank">` — the browser's own image view: no
// paging, no download, and on a phone no way back except the OS back gesture.
// A crew member checking a before-photo had to leave the app and return once
// per photo.
//
// It is ONE component for both screens on purpose. The office job panel and
// the phone job screen show the same photos off the same table, and the
// classic app's two viewers is how "Download" ended up on one of them
// (office/insight/Media.js) and not the other.
//
// ── Phone rules it is built to (docs/APP_REBUILD.md) ────────────────────────
//
// There is not one `px` width or min-width in MediaViewer.css. The image is
// `max-width: 100%` inside a column that is `100%` of a `100dvh` overlay, the
// controls are `min(…)` clamps against the viewport, and the caption strip
// wraps. A lightbox is exactly where a fixed width creeps in — the classic one
// had `min-width: 320px` on its info bar — and a fixed width here would pan
// the whole page sideways at 320px.
//
// Keyboard: Escape closes, ArrowLeft / ArrowRight page, and both wrap, which
// is what the classic viewer did. Focus goes to Close on open and back to
// whatever opened it on unmount, so a keyboard is never stranded behind the
// overlay.

import React, { useCallback, useEffect, useRef } from 'react';
import { Icon } from '../../../ui';
import { fileHref, fileLabel, fmtBytes } from './mediaKinds';
import './MediaViewer.css';

/**
 * @param items  the images, in the order they are shown
 * @param index  which one is open
 * @param onIndex(next) page to another
 * @param onClose close it
 */
export default function MediaViewer({ items = [], index = 0, onIndex, onClose }) {
  const closeRef = useRef(null);
  const lastFocused = useRef(null);
  const count = items.length;
  // Clamped rather than trusted: a delete while the viewer is open would
  // otherwise index past the end and render a blank overlay with no image and
  // no way to tell that from a photo that failed to load.
  const at = count === 0 ? 0 : Math.min(Math.max(index, 0), count - 1);
  const item = items[at] || null;

  const go = useCallback((delta) => {
    if (!count || !onIndex) return;
    onIndex((at + delta + count) % count);
  }, [at, count, onIndex]);

  useEffect(() => {
    lastFocused.current = typeof document !== 'undefined' ? document.activeElement : null;
    if (closeRef.current && closeRef.current.focus) closeRef.current.focus();
    return () => {
      const el = lastFocused.current;
      lastFocused.current = null;
      if (el && el.focus) { try { el.focus(); } catch (_) { /* gone */ } }
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); if (onClose) onClose(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [go, onClose]);

  if (!item) return null;
  const name = fileLabel(item);
  const href = fileHref(item);

  return (
    <div
      className="jv"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
    >
      <div className="jv__panel">
        <div className="jv__bar">
          <span className="jv__counter fw-num">{at + 1} of {count}</span>
          <a
            className="jv__btn jv__btn--link"
            href={href}
            download={name}
            aria-label={`Download ${name}`}
          >
            <Icon name="download" /> <span>Download</span>
          </a>
          <button
            type="button"
            ref={closeRef}
            className="jv__btn"
            onClick={onClose}
            aria-label="Close viewer"
          >
            <Icon name="close" /> <span>Close</span>
          </button>
        </div>

        <div className="jv__stage">
          {count > 1 ? (
            <button type="button" className="jv__nav jv__nav--prev" onClick={() => go(-1)} aria-label="Previous photo">
              <Icon name="chevronLeft" />
            </button>
          ) : null}
          {/* Keyed by id so paging swaps the element rather than mutating one
              src — a browser keeps painting the OLD decoded image until the
              next one arrives, which reads as Next doing nothing. */}
          <img key={item.id || at} className="jv__img" src={href} alt={item.caption || name} />
          {count > 1 ? (
            <button type="button" className="jv__nav jv__nav--next" onClick={() => go(1)} aria-label="Next photo">
              <Icon name="chevronRight" />
            </button>
          ) : null}
        </div>

        <div className="jv__info">
          <div className="jv__name">{name}</div>
          {item.caption ? <div className="jv__caption">{item.caption}</div> : null}
          <div className="jv__meta">
            {[item.uploaded_by_name || item.user_name, fmtBytes(item.file_size)]
              .filter((x) => x !== null && x !== undefined && x !== '' && x !== '—')
              .join(' · ')}
          </div>
        </div>
      </div>
    </div>
  );
}
